import React, { useEffect, useState } from 'react';
import { compose, graphql } from 'react-apollo';

import EditScoring from './EditScoring';

import { useScoring } from './ScoringProvider';
import { getRubricData } from './EditScoringQueries';
import useConvertHTMLToProsemirrorJSON from './useProseConverterEffects';
import scoringRubricSchema from './EditScoringDataSchema';
import { validationActionTypes } from '../../constants';

const EditScoringContainer = ({
  learnosityItemReference,
  dispatchValidation,
  rubricData,
  showValidation,
  validations,
}) => {
  const [itemRubric] = useConvertHTMLToProsemirrorJSON(rubricData);
  const [scoreState, dispatch] = useScoring();
  const [innerErrors, setInnerErrors] = useState([]);

  const updatePoint = (option, sectionIndex, pointIndex) => {
    const { feedback, additional_feedback } = option;
    dispatch({ type: 'addPoint', payload: { sectionIndex } });
    dispatch({
      type: 'updatePointScoringFeedback',
      payload: { feedback, index: pointIndex, sectionIndex },
    });
    dispatch({
      type: 'updatePointScoringAdditionalFeedback',
      payload: { additional_feedback, index: pointIndex, sectionIndex },
    });
  };

  const updateCriteria = (
    criteria,
    sectionIndex,
    pointIndex,
    criteriaIndex,
  ) => {
    dispatch({
      type: 'addCriteria',
      payload: { sectionIndex, pointIndex },
    });

    const { description } = criteria;
    dispatch({
      type: 'updateCriteria',
      payload: {
        description,
        sectionIndex,
        pointIndex,
        index: criteriaIndex,
      },
    });
  };

  const updateScoring = (sectionIndex) => {
    const { title, stimulus, options } = itemRubric[sectionIndex];
    dispatch({ type: 'addEmptyScoringSection' });
    dispatch({
      type: 'updateScoringSectionTitle',
      payload: { sectionTitle: title, index: sectionIndex },
    });
    dispatch({
      type: 'updateScoringSectionInstructions',
      payload: { scoringInstructions: stimulus, index: sectionIndex },
    });

    options.forEach((option, pointIndex) => {
      updatePoint(option, sectionIndex, pointIndex);
      option.criterias.forEach((criteria, criteriaIndex) => {
        updateCriteria(criteria, sectionIndex, pointIndex, criteriaIndex);
      });
    });
  };

  useEffect(() => {
    if (itemRubric && itemRubric.length) {
      dispatch({ type: 'cleanupScoring' });
      dispatch({
        type: 'setItemReference',
        payload: { itemReference: learnosityItemReference },
      });

      itemRubric.forEach((_, sectionIndex) => {
        updateScoring(sectionIndex);
      });
    }
  }, [itemRubric]);

  useEffect(
    () =>
      dispatch({
        type: 'setItemReference',
        payload: { itemReference: learnosityItemReference },
      }),
    [learnosityItemReference],
  );

  useEffect(() => {
    scoringRubricSchema
      .validate(scoreState.scoreState, { abortEarly: false })
      .then(() => {
        setInnerErrors([]);
        dispatchValidation({
          type: validationActionTypes.scoringSectionErrors,
          payload: { errors: [] },
        });
      })
      .catch((e) => {
        setInnerErrors(e.inner);
        dispatchValidation({
          type: validationActionTypes.scoringSectionErrors,
          payload: { errors: e.errors, validationError: e },
        });
      });
  }, [scoreState]);

  return (
    <EditScoring
      showValidation={showValidation}
      validations={validations}
      innerErrors={innerErrors}
    />
  );
};

const withRubricData = graphql(getRubricData, {
  options: ({ learnosityItemReference }) => ({
    fetchPolicy: 'network-only',
    variables: {
      assignmentId: 0,
      itemReference: learnosityItemReference,
    },
  }),
  props: ({ data, ownProps }) => {
    const { loading, rubricData, error } = data;

    if (loading) return null;

    // set rubric data on edit only, for new items rubric data does not exists
    if (ownProps.isEditingItem) {
      return {
        rubricData,
        error,
      };
    }
  },
});

export default compose(withRubricData)(EditScoringContainer);
